import React, { useState, FC } from "react";

import { Button, Icon, Popover, SheetContainer, Stack, Text } from "@bookingcom/bui-react";
import { SVG } from "@bookingcom/bui-react/components/Icon/Icon.types";
import { t, useI18n } from "@bookingcom/lingojs-react";
import Frame from "../../Frame";

type Props = {
  amenities: Amenity[];
  variant?: "popover" | "inline" | "modal";
  onPopoverOpen?: VoidFunction;
};

type Amenity = { svg: SVG; copy: string };

const TimelineLegAmenities: FC<Props> = ({ amenities, variant = "popover", onPopoverOpen }) => {
  if (amenities.length === 0) {
    return null;
  }

  if (variant === "popover") {
    return <AmenitiesPopover amenities={amenities} onPopoverOpen={onPopoverOpen} />;
  } else if (variant === "modal") {
    return <AmenitiesModal amenities={amenities} />;
  } else if (variant === "inline") {
    return <AmenitiesInline amenities={amenities} />;
  } else {
    return null;
  }
};

const AmenitiesPopover: FC<{ amenities: Amenity[]; onPopoverOpen?: VoidFunction }> = ({ amenities, onPopoverOpen }) => {
  const i18n = useI18n();

  return (
    <Popover
      position="bottom-start"
      closeAriaLabel={i18n.trans(t("a11y_flights_mdot_fd_rh_bottomsheet_close"))}
      onAfterOpen={onPopoverOpen}
      triggerType="hover"
    >
      <Popover.Trigger>
        {(attributes) => (
          <Button.Aligner alignment="start">
            <Button attributes={{ ...attributes }} variant="tertiary-neutral">
              <Stack direction="row" gap={2}>
                {amenities.map(({ svg }, i) => {
                  return <Icon key={i} size="medium" svg={svg} />;
                })}
              </Stack>
            </Button>
          </Button.Aligner>
        )}
      </Popover.Trigger>
      <Popover.Content>
        <Stack gap={2}>
          {amenities.map(({ svg, copy }, i) => {
            return (
              <Stack key={i} direction="row" gap={3} wrap="nowrap">
                <Icon size="medium" svg={svg} />
                <Stack.Item shrink={true}>
                  <Text>{copy}</Text>
                </Stack.Item>
              </Stack>
            );
          })}
        </Stack>
      </Popover.Content>
    </Popover>
  );
};

const AmenitiesInline: FC<{ amenities: Amenity[] }> = ({ amenities }) => {
  return (
    <Frame pt={2}>
      <Stack gap={1}>
        {amenities.map(({ svg, copy }, i) => {
          return (
            <Stack key={i} direction="row" gap={3} wrap="nowrap">
              <Icon size="small" svg={svg} />
              <Stack.Item shrink={true}>
                <Text variant="small_1">{copy}</Text>
              </Stack.Item>
            </Stack>
          );
        })}
      </Stack>
    </Frame>
  );
};

const AmenitiesModal: FC<{ amenities: Amenity[] }> = ({ amenities }) => {
  const i18n = useI18n();
  const [active, setActive] = useState(false);

  return (
    <>
      <Frame pt={1}>
        <Stack direction="row" gap={2}>
          {amenities.map(({ svg }, i) => {
            return <Icon key={i} size="small" svg={svg} />;
          })}
        </Stack>
        <Button.Aligner alignment="start">
          <Button onClick={() => setActive(true)} variant="tertiary">
            {i18n.trans(t("flights_mdot_fd_route_happy_cta"))}
          </Button>
        </Button.Aligner>
      </Frame>

      <SheetContainer
        title={i18n.trans(t("flights_mdot_fd_rh_bottomsheet_title"))}
        active={active}
        closeAriaLabel={i18n.trans(t("a11y_flights_mdot_fd_rh_bottomsheet_close"))}
        onCloseTrigger={() => setActive(false)}
      >
        <Stack gap={2}>
          {amenities.map(({ svg, copy }, i) => {
            return (
              <Stack key={i} direction="row" gap={3} wrap="nowrap">
                <Icon size="medium" svg={svg} />
                <Stack.Item shrink={true}>
                  <Text>{copy}</Text>
                </Stack.Item>
              </Stack>
            );
          })}
        </Stack>
      </SheetContainer>
    </>
  );
};

export default TimelineLegAmenities;
